import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Button,
  TextField,
  Popover,
  Paper,
  InputAdornment,
  Checkbox,
  Chip,
  Grid,
  Autocomplete,
  CircularProgress,
  Menu,
  MenuItem,
  useMediaQuery,
  useTheme,
  alpha,
} from "@mui/material";
import { ExpandMore, ExpandLess } from "@mui/icons-material";
import { Collapse, Divider } from "@mui/material";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Search,
  Print,
  Download,
  Edit,
  Delete,
  Visibility,
} from "@mui/icons-material";
import {
  fetchpos,
  deletepos,
  ApprovePO,
  fetchSuppliers,
} from "../../../Services/Services";
import { useNavigate } from "react-router-dom";
import AddIcon from "@mui/icons-material/Add";
import { Tooltip } from "@mui/material";
import axios from "axios";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
import { handlePrint } from "./PurchaseOrderPrint";
import InfiniteScroll from "react-infinite-scroll-component";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";

pdfMake.vfs = pdfFonts.vfs;

const calculateValue = (item) => {
  let val = item.qty * item.price - item.discount;
  return (val + (val * item.tax) / 100).toFixed(2);
};

const getStatusMeta = (status) => {
  const s = String(status || "pending").toLowerCase();
  if (s === "approved")
    return { label: "Approved", color: "#2e7d32", bg: "#e8f5e9" };
  if (s === "received" || s === "recieved")
    return { label: "Received", color: "#0288d1", bg: "#e1f5fe" };
  if (s === "cancelled")
    return { label: "Cancelled", color: "#d32f2f", bg: "#ffebee" };
  return { label: "Pending", color: "#ed6c02", bg: "#fff3e0" };
};

const StatusChip = ({ status }) => {
  const meta = getStatusMeta(status);
  return (
    <Chip
      size="small"
      label={meta.label}
      sx={{
        height: 22,
        fontSize: "0.68rem",
        fontWeight: 700,
        color: meta.color,
        bgcolor: meta.bg,
        border: `1px solid ${meta.color}33`,
      }}
    />
  );
};

// ── Status border color helper ──
const getStatusBorderColor = (approvalStatus) => {
  if (approvalStatus === "Approved") return "#4caf50";
  if (approvalStatus === "Reject") return "#f44336";
  return "#ff9800";
};

const ProjectList = () => {
  const [po, setpos] = useState([]);
  const [projects, setProjects] = useState([]);
  const [openRow, setOpenRow] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [poToDelete, setPoToDelete] = useState(null);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [approveAnchorEl, setApproveAnchorEl] = useState(null);
  const [poToApprove, setPoToApprove] = useState(null);
  const [remarkInput, setRemarkInput] = useState("");
  const [actionType, setActionType] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [showRemarkForPoId, setShowRemarkForPoId] = useState(null);
  const [filters, setFilters] = useState({
    po_number: "",
    supplier: "",
    date_range: "today",
  });
  const [showCustomDateInputs, setShowCustomDateInputs] = useState(false);
  const [tempCustomDateRange, setTempCustomDateRange] = useState({
    from: "",
    to: "",
  });
  const [dateMenuAnchor, setDateMenuAnchor] = useState(null);
  const [customDateRange, setCustomDateRange] = useState({
    from: "",
    to: "",
  });

  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [expandedPO, setExpandedPO] = useState(null);

  // ── Theme-aware helpers ──
  const tableHeadBg = isDark
    ? alpha(theme.palette.primary.main, 0.15)
    : "#f0f4ff";

  const rowBg = (index) =>
    index % 2 === 0
      ? isDark
        ? alpha(theme.palette.common.white, 0.035)
        : "#fafafa"
      : theme.palette.background.paper;

  // ── Django Print ──
  const handleDjangoPrint = (poId) => {
    const djangoBaseUrl =
      process.env.REACT_APP_API_URL || "http://127.0.0.1:8000";
    window.open(`${djangoBaseUrl}/print/purchaseorder/${poId}/`, "_blank");
  };

  // ── Date range helpers ──
  const getDateRangeDisplay = (rangeType) => {
    const now = new Date();
    const formatDate = (date) => {
      const day = date.getDate().toString().padStart(2, "0");
      const month = (date.getMonth() + 1).toString().padStart(2, "0");
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    };
    switch (rangeType) {
      case "today":
        return formatDate(now);
      case "yesterday":
        const yesterday = new Date(now);
        yesterday.setDate(yesterday.getDate() - 1);
        return formatDate(yesterday);
      case "this_week":
        const weekStart = new Date(now);
        weekStart.setDate(weekStart.getDate() - weekStart.getDay() + 1);
        return `${formatDate(weekStart)} to ${formatDate(now)}`;
      case "previous_week":
        const prevWeekEnd = new Date(now);
        prevWeekEnd.setDate(prevWeekEnd.getDate() - prevWeekEnd.getDay());
        const prevWeekStart = new Date(prevWeekEnd);
        prevWeekStart.setDate(prevWeekStart.getDate() - 6);
        return `${formatDate(prevWeekStart)} to ${formatDate(prevWeekEnd)}`;
      case "this_month":
        const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
        return `${formatDate(monthStart)} to ${formatDate(now)}`;
      case "current_year":
        const yearStart = new Date(now.getFullYear(), 0, 1);
        return `${formatDate(yearStart)} to ${formatDate(now)}`;
      case "financial_year":
        let fyStart;
        if (now.getMonth() >= 3) {
          fyStart = new Date(now.getFullYear(), 3, 1);
        } else {
          fyStart = new Date(now.getFullYear() - 1, 3, 1);
        }
        return `${formatDate(fyStart)} to ${formatDate(now)}`;
      default:
        return "";
    }
  };

  const formatDateDisplay = (dateString) => {
    if (!dateString) return "";
    const [year, month, day] = dateString.split("-");
    return `${day}-${month}-${year}`;
  };

  const handleReset = () => {
    setFilters({ po_number: "", supplier: "", date_range: "today" });
    setCustomDateRange({ from: "", to: "" });
    setTempCustomDateRange({ from: "", to: "" });
    setShowCustomDateInputs(false);
  };

  const handleApplyCustomDateRange = () => {
    if (tempCustomDateRange.from && tempCustomDateRange.to) {
      setCustomDateRange({
        from: tempCustomDateRange.from,
        to: tempCustomDateRange.to,
      });
      setFilters((prev) => ({ ...prev, date_range: "custom" }));
      setDateMenuAnchor(null);
      setShowCustomDateInputs(false);
    } else {
      toast.warning("Please select both From and To dates");
    }
  };

  const handleCancelCustomDateRange = () => {
    setTempCustomDateRange({ from: "", to: "" });
    setShowCustomDateInputs(false);
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      setPage(1);
      const updatedFilters = {
        ...filters,
        from_date: customDateRange.from || "",
        to_date: customDateRange.to || "",
      };
      loadPo(1, true, updatedFilters);
    }, 1000);
    return () => clearTimeout(handler);
  }, [filters, customDateRange]);

  useEffect(() => {
    const loadSuppliers = async () => {
      try {
        const data = await fetchSuppliers();
        setSuppliers(Array.isArray(data) ? data : data.results || []);
      } catch (error) {
        console.error("Failed to load suppliers", error);
      }
    };
    loadSuppliers();
  }, []);

  const loadPo = async (
    pageNum = 1,
    reset = false,
    filterValues = filters
  ) => {
    try {
      const data = await fetchpos(filterValues, pageNum);
      if (reset || pageNum === 1) {
        setProjects(Array.isArray(data) ? data.results : data.results || []);
      } else {
        setProjects((prev) => [
          ...prev,
          ...(Array.isArray(data) ? data.results : data.results || []),
        ]);
      }
      setHasMore(
        Array.isArray(data) ? data.next !== null : data.next !== null
      );
      setPage(pageNum);
    } catch (error) {
      console.error("Failed to load po", error);
      toast.error("Failed to load po");
    }
  };

  const fetchMoreData = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    const updatedFilters = {
      ...filters,
      from_date: customDateRange.from || "",
      to_date: customDateRange.to || "",
    };
    loadPo(nextPage, false, updatedFilters);
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleApprove = async (po) => {
    const approvedById = Number(localStorage.getItem("user_id"));
    if (!approvedById) {
      toast.error("Approver ID not found.");
      return;
    }
    const isApproval = po.approval_status === "Approved";
    try {
      const updated = await ApprovePO(
        po.po_id,
        approvedById,
        po.approval_status,
        remarkInput
      );
      setpos((prev) =>
        prev.map((item) => (item.po_id === po.po_id ? updated : item))
      );
      const UpdtedList = await fetchpos(filters);
      setProjects(UpdtedList.results);
      if (isApproval) {
        toast.success("PO approved successfully!");
      } else {
        toast.warning("PO rejected!");
      }
    } catch (error) {
      console.error("Error:", error);
      toast.error(
        isApproval ? "Failed to approve PO." : "Failed to reject PO."
      );
    }
  };

  const isPoWithoutIndent = (po) => {
    if (typeof po.is_without_indent !== "undefined")
      return !!po.is_without_indent;
    if (typeof po.po_type !== "undefined")
      return String(po.po_type).toLowerCase().includes("without");
    return !po.indent && !po.indent_id;
  };

  const handleEdit = (po) => {
    const path = isPoWithoutIndent(po)
      ? "/dashboard/powithoutindent"
      : "/dashboard/po-form";
    navigate(path, { state: { poData: po, isEdit: true } });
  };

  const handleDeleteClick = (po) => {
    setPoToDelete(po);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    try {
      await deletepos(poToDelete.po_id);
      setProjects((prev) =>
        prev.filter((p) => p.po_id !== poToDelete.po_id)
      );
      setPoToDelete(null);
      toast.success("Purchase order deleted successfully");
    } catch (error) {
      console.error("Delete failed", error);
      toast.error("Failed to delete purchase order");
    }
  };

  const handleCreateWithIndent = () => {
    navigate("/dashboard/po-form");
  };

  const handleCreateWithoutIndent = () => {
    navigate("/dashboard/powithoutindent");
  };

  const handleClick = () => {
    navigate("/dashboard/po-form");
  };

  const truncateText = (text, limit = 20) => {
    if (!text) return "";
    return text.length > limit ? text.substring(0, limit) + "..." : text;
  };

  const userRole = localStorage.getItem("role");

  function formatOnlyDate(dateTimeString) {
    if (!dateTimeString) return "N/A";
    const datePart = dateTimeString.split("T")[0];
    const [year, month, day] = datePart.split("-");
    return `${day}-${month}-${year}`;
  }

  return (
    <Box
      sx={{
        px: 0,
        pt: 0,
        mt: { xs: 4, md: 1 },
        maxWidth: 1200,
        mx: "auto",
        bgcolor: "background.default",
        minHeight: "100%",
      }}
    >
      {/* ── Filters Bar ── */}
      <Box
        sx={{
          pt: 1,
          pb: 1,
          mb: 1,
          display: "flex",
          flexDirection: "column",
          gap: 1,
          position: "sticky",
          top: 0,
          zIndex: 1100,
          // borderBottom: "1px solid",
          // borderColor: "divider",
          // bgcolor: (t) => alpha(t.palette.background.paper, 0.95),
          // backdropFilter: "blur(8px)",
          // boxShadow: isDark
          //   ? "0 2px 10px rgba(0,0,0,0.45)"
          //   : "0 1px 4px rgba(0,0,0,0.07)",
        }}
      >
        <Grid
          container
          spacing={1}
          alignItems="center"
          sx={{ flexWrap: { xs: "wrap", sm: "nowrap" } }}
        >
          {/* Date Range */}
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              label="Date Range"
              value={
                filters.date_range === "custom" &&
                customDateRange.from &&
                customDateRange.to
                  ? `${formatDateDisplay(customDateRange.from)} to ${formatDateDisplay(customDateRange.to)}`
                  : filters.date_range === "today"
                  ? "Today"
                  : filters.date_range === "yesterday"
                  ? "Yesterday"
                  : filters.date_range === "this_week"
                  ? "This Week"
                  : filters.date_range === "previous_week"
                  ? "Previous Week"
                  : filters.date_range === "this_month"
                  ? "This Month"
                  : filters.date_range === "current_year"
                  ? "Current Year"
                  : filters.date_range === "financial_year"
                  ? "Financial Year"
                  : "Select Date Range"
              }
              onClick={(e) => setDateMenuAnchor(e.currentTarget)}
              size="small"
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <IconButton size="small" edge="end">
                    <ArrowDropDownIcon />
                  </IconButton>
                ),
              }}
              sx={{
                cursor: "pointer",
                "& .MuiInputBase-input": {
                  fontSize: "0.75rem",
                  padding: "4px 8px",
                },
                "& .MuiInputBase-root": { height: 32, borderRadius: 0 },
                "& .MuiInputLabel-root": { fontSize: "0.7rem" },
              }}
            />
          </Grid>

          {/* PO Number */}
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              label="Search PO Number"
              size="small"
              name="po_number"
              value={filters.po_number}
              onChange={handleFilterChange}
              sx={{
                "& .MuiInputBase-root": { height: 32, borderRadius: 0 },
                "& .MuiInputLabel-root": { fontSize: "0.7rem" },
              }}
            />
          </Grid>

          {/* Supplier */}
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              label="Search Supplier"
              size="small"
              name="supplier"
              value={filters.supplier}
              onChange={handleFilterChange}
              sx={{
                "& .MuiInputBase-root": { height: 32, borderRadius: 0 },
                "& .MuiInputLabel-root": { fontSize: "0.7rem" },
              }}
            />
          </Grid>

          <Grid
            item
            sx={{ flexGrow: 1, display: { xs: "none", sm: "block" } }}
          />

          {/* Reset */}
          <Grid item xs={6} sm="auto">
            <Button
              fullWidth
              variant="outlined"
              color="primary"
              size="small"
              onClick={handleReset}
              sx={{
                height: 32,
                fontSize: { xs: "0.65rem", sm: "0.7rem" },
                borderRadius: 0,
                px: 2,
                minWidth: { sm: 70 },
                whiteSpace: "nowrap",
              }}
            >
              Reset
            </Button>
          </Grid>

          {/* Add PO With Indent - Commented out for now */}
          {/* <Grid item xs={6} sm="auto">
            <Button
              fullWidth
              variant="outlined"
              color="primary"
              onClick={handleCreateWithIndent}
              startIcon={<AddIcon />}
              sx={{
                height: 32,
                fontSize: { xs: "0.6rem", sm: "0.7rem" },
                borderRadius: 0,
                px: { xs: 1, sm: 2 },
                minWidth: { sm: 140 },
                whiteSpace: "nowrap",
              }}
            >
              PO (With Indent)
            </Button>
          </Grid> */}

          {/* Purchase Order (Direct PO) */}
          <Grid item xs={6} sm="auto">
            <Button
              fullWidth
              variant="contained"
              color="primary"
              onClick={handleCreateWithoutIndent}
              startIcon={<AddIcon />}
              sx={{
                height: 32,
                fontSize: { xs: "0.6rem", sm: "0.7rem" },
                borderRadius: 0,
                px: { xs: 1, sm: 2 },
                minWidth: { sm: 140 },
                whiteSpace: "nowrap",
              }}
            >
              Purchase Order
            </Button>
          </Grid>
        </Grid>

        {/* Date Range Menu */}
        <Menu
          anchorEl={dateMenuAnchor}
          open={Boolean(dateMenuAnchor)}
          onClose={() => {
            setDateMenuAnchor(null);
            setShowCustomDateInputs(false);
            setTempCustomDateRange({ from: "", to: "" });
          }}
          PaperProps={{
            sx: {
              width: 350,
              maxHeight: 500,
              bgcolor: "background.paper",
              backgroundImage: "none",
              border: "1px solid",
              borderColor: "divider",
              boxShadow: isDark
                ? "0 8px 28px rgba(0,0,0,0.6)"
                : "0 4px 16px rgba(0,0,0,0.14)",
              "& .MuiTypography-root": { fontSize: "0.7rem" },
            },
          }}
        >
          {[
            { key: "today", label: "Today" },
            { key: "yesterday", label: "Yesterday" },
            { key: "this_week", label: "This Week" },
            { key: "previous_week", label: "Previous Week" },
            { key: "this_month", label: "This Month" },
            { key: "current_year", label: "Current Year" },
            { key: "financial_year", label: "Financial Year" },
          ].map((range) => (
            <MenuItem
              key={range.key}
              onClick={() => {
                setFilters((prev) => ({
                  ...prev,
                  date_range: range.key,
                }));
                setCustomDateRange({ from: "", to: "" });
                setDateMenuAnchor(null);
                setShowCustomDateInputs(false);
              }}
              sx={{
                "&:hover": {
                  bgcolor: (t) =>
                    t.palette.mode === "dark"
                      ? alpha(t.palette.primary.main, 0.1)
                      : alpha(t.palette.primary.main, 0.06),
                },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  width: "100%",
                }}
              >
                <Typography variant="body2" sx={{ color: "text.primary" }}>
                  {range.label}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ fontSize: "0.65rem", color: "text.secondary" }}
                >
                  {getDateRangeDisplay(range.key)}
                </Typography>
              </Box>
            </MenuItem>
          ))}

          <Divider />

          <MenuItem
            onClick={() => setShowCustomDateInputs(!showCustomDateInputs)}
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              bgcolor: showCustomDateInputs
                ? (t) =>
                    t.palette.mode === "dark"
                      ? alpha(t.palette.primary.main, 0.15)
                      : "#e3f2fd"
                : "inherit",
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontWeight: showCustomDateInputs ? 600 : 400,
                color: "text.primary",
              }}
            >
              Custom Date Range
            </Typography>
            {showCustomDateInputs ? <ExpandLess /> : <ExpandMore />}
          </MenuItem>

          <Collapse in={showCustomDateInputs} timeout="auto" unmountOnExit>
            <Box
              sx={{
                px: 2,
                py: 2,
                bgcolor: (t) =>
                  t.palette.mode === "dark"
                    ? alpha(t.palette.common.white, 0.04)
                    : "#f5f5f5",
                borderTop: "1px solid",
                borderColor: "divider",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <Typography
                variant="caption"
                sx={{ mb: 1, display: "block", color: "text.secondary" }}
              >
                Select Date Range
              </Typography>

              <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
                <TextField
                  label="From Date"
                  type="date"
                  size="small"
                  value={tempCustomDateRange.from}
                  onChange={(e) =>
                    setTempCustomDateRange((prev) => ({
                      ...prev,
                      from: e.target.value,
                    }))
                  }
                  InputLabelProps={{ shrink: true }}
                  inputProps={{
                    max:
                      tempCustomDateRange.to ||
                      new Date().toISOString().split("T")[0],
                  }}
                  sx={{
                    flex: 1,
                    "& .MuiInputBase-root": {
                      height: 40,
                      fontSize: "0.75rem",
                    },
                    "& .MuiInputLabel-root": { fontSize: "0.75rem" },
                  }}
                />
                <TextField
                  label="To Date"
                  type="date"
                  size="small"
                  value={tempCustomDateRange.to}
                  onChange={(e) =>
                    setTempCustomDateRange((prev) => ({
                      ...prev,
                      to: e.target.value,
                    }))
                  }
                  InputLabelProps={{ shrink: true }}
                  inputProps={{
                    min: tempCustomDateRange.from || undefined,
                    max: new Date().toISOString().split("T")[0],
                  }}
                  sx={{
                    flex: 1,
                    "& .MuiInputBase-root": {
                      height: 40,
                      fontSize: "0.75rem",
                    },
                    "& .MuiInputLabel-root": { fontSize: "0.75rem" },
                  }}
                />
              </Box>

              {tempCustomDateRange.from && tempCustomDateRange.to && (
                <Typography
                  variant="caption"
                  sx={{
                    display: "block",
                    mb: 1.5,
                    p: 1,
                    bgcolor: (t) =>
                      t.palette.mode === "dark"
                        ? alpha(t.palette.success.main, 0.15)
                        : "#e8f5e9",
                    borderRadius: 1,
                    textAlign: "center",
                    color: "success.main",
                    fontWeight: 500,
                  }}
                >
                  📅 {formatDateDisplay(tempCustomDateRange.from)} to{" "}
                  {formatDateDisplay(tempCustomDateRange.to)}
                </Typography>
              )}

              <Box
                sx={{
                  display: "flex",
                  gap: 1,
                  justifyContent: "flex-end",
                }}
              >
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleCancelCustomDateRange}
                  sx={{ fontSize: "0.7rem", textTransform: "none", borderRadius: 1 }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleApplyCustomDateRange}
                  disabled={
                    !tempCustomDateRange.from || !tempCustomDateRange.to
                  }
                  sx={{ fontSize: "0.7rem", textTransform: "none", borderRadius: 1 }}
                >
                  Apply
                </Button>
              </Box>
            </Box>
          </Collapse>
        </Menu>
      </Box>

      {/* ── Infinite Scroll ── */}
      <InfiniteScroll
        dataLength={projects.length}
        next={fetchMoreData}
        hasMore={hasMore}
        scrollableTarget="main-scroll-container"
        loader={
          <Typography
            sx={{
              textAlign: "center",
              p: 1,
              fontSize: "0.8rem",
              color: "text.secondary",
            }}
          >
            Loading...
          </Typography>
        }
        endMessage={
          <Typography
            sx={{
              textAlign: "center",
              p: 1,
              fontSize: "0.75rem",
              color: "text.disabled",
            }}
          >
            No more purchase orders
          </Typography>
        }
      >
        {isMobile ? (
          /* ── MOBILE CARDS ── */
          <Box sx={{ bgcolor: "background.paper" }}>
            {projects.map((po, index) => (
              <Box key={po.id}>
                {/* Main Row */}
                <Box
                  onClick={() =>
                    setExpandedPO(expandedPO === po.id ? null : po.id)
                  }
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    p: 1.5,
                    cursor: "pointer",
                    bgcolor: rowBg(index),
                    borderLeft: `4px solid ${getStatusBorderColor(
                      po.approval_status
                    )}`,
                    transition: "background-color 0.15s ease",
                    "&:active": { bgcolor: "action.selected" },
                  }}
                >
                  <Box sx={{ flex: 1 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mb: 0.5,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "0.7rem",
                          color: "text.disabled",
                          minWidth: 25,
                        }}
                      >
                        #{index + 1}
                      </Typography>
                      <Typography
                        sx={{
                          fontWeight: "bold",
                          fontSize: "0.85rem",
                          flex: 1,
                          color: "text.primary",
                        }}
                      >
                        {po.po_number}
                      </Typography>
                      <StatusChip status={po.status} />
                    </Box>

                    <Typography
                      sx={{
                        fontSize: "0.75rem",
                        color: "text.secondary",
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <span style={{ fontWeight: 500 }}>
                        {suppliers
                          .find((s) => s.id === po.supplier)
                          ?.name?.substring(0, 20) ||
                          `Supplier ${po.supplier}`}
                      </span>
                      <span>•</span>
                      <span style={{ fontSize: "0.7rem" }}>
                        {formatOnlyDate(po.created_at)}
                      </span>
                    </Typography>
                  </Box>

                  <IconButton size="small" sx={{ ml: 1 }}>
                    {expandedPO === po.id ? (
                      <ExpandLess fontSize="small" />
                    ) : (
                      <ExpandMore fontSize="small" />
                    )}
                  </IconButton>
                </Box>

                {/* Expanded Details */}
                <Collapse
                  in={expandedPO === po.id}
                  timeout="auto"
                  unmountOnExit
                >
                  <Box
                    sx={{
                      px: 1.5,
                      py: 1,
                      bgcolor: (t) =>
                        t.palette.mode === "dark"
                          ? alpha(t.palette.common.white, 0.05)
                          : alpha(t.palette.primary.main, 0.04),
                      borderLeft: `4px solid ${getStatusBorderColor(
                        po.approval_status
                      )}`,
                    }}
                  >
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: 0.8,
                        mb: 1,
                      }}
                    >
                      <Box sx={{ gridColumn: "1 / -1" }}>
                        <Typography
                          sx={{
                            fontSize: "0.6rem",
                            color: "text.disabled",
                            textTransform: "uppercase",
                            letterSpacing: "0.08em",
                            fontWeight: 600,
                          }}
                        >
                          SUPPLIER
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "0.75rem",
                            fontWeight: 500,
                            color: "text.primary",
                          }}
                        >
                          {suppliers.find((s) => s.id === po.supplier)?.name ||
                            `Supplier ${po.supplier}`}
                        </Typography>
                      </Box>

                      <Box>
                        <Typography
                          sx={{
                            fontSize: "0.6rem",
                            color: "text.disabled",
                            textTransform: "uppercase",
                            letterSpacing: "0.08em",
                            fontWeight: 600,
                          }}
                        >
                          STATUS
                        </Typography>
                        <StatusChip status={po.status} />
                      </Box>

                      {po.created_by_name && (
                        <Box>
                          <Typography
                            sx={{
                              fontSize: "0.6rem",
                              color: "text.disabled",
                              textTransform: "uppercase",
                              letterSpacing: "0.08em",
                              fontWeight: 600,
                            }}
                          >
                            CREATED BY
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "0.72rem",
                              fontWeight: 500,
                              color: "text.secondary",
                            }}
                          >
                            {po.created_by_name}
                          </Typography>
                        </Box>
                      )}

                      {po.approved_name && (
                        <Box>
                          <Typography
                            sx={{
                              fontSize: "0.6rem",
                              color: "text.disabled",
                              textTransform: "uppercase",
                              letterSpacing: "0.08em",
                              fontWeight: 600,
                            }}
                          >
                            APPROVED BY
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "0.72rem",
                              fontWeight: 500,
                              color: "text.secondary",
                            }}
                          >
                            {po.approved_name}
                          </Typography>
                        </Box>
                      )}
                    </Box>

                    <Box sx={{ mb: 0.8 }}>
                      <Typography
                        sx={{
                          fontSize: "0.6rem",
                          color: "text.disabled",
                          textTransform: "uppercase",
                          letterSpacing: "0.08em",
                          fontWeight: 600,
                          mb: 0.3,
                        }}
                      >
                        BILLING ADDRESS
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "0.7rem",
                          color: "text.secondary",
                          lineHeight: 1.3,
                        }}
                      >
                        {po.billing_address}
                      </Typography>
                    </Box>

                    <Box sx={{ mb: 0.8 }}>
                      <Typography
                        sx={{
                          fontSize: "0.6rem",
                          color: "text.disabled",
                          textTransform: "uppercase",
                          letterSpacing: "0.08em",
                          fontWeight: 600,
                          mb: 0.3,
                        }}
                      >
                        SHIPPING ADDRESS
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "0.7rem",
                          color: "text.secondary",
                          lineHeight: 1.3,
                        }}
                      >
                        {po.shipping_address}
                      </Typography>
                    </Box>

                    <Divider sx={{ my: 1, borderColor: "divider" }} />

                    {/* Mobile Actions */}
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      {userRole != "Staff" && (
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            setApproveAnchorEl(true);
                            setPoToApprove(po);
                          }}
                          sx={{
                            bgcolor: (t) =>
                              t.palette.mode === "dark"
                                ? alpha(t.palette.primary.main, 0.15)
                                : "#e3f2fd",
                            borderRadius: "6px",
                            width: 32,
                            height: 32,
                            "&:hover": {
                              bgcolor: (t) =>
                                t.palette.mode === "dark"
                                  ? alpha(t.palette.primary.main, 0.28)
                                  : "#bbdefb",
                            },
                          }}
                        >
                          <Visibility
                            sx={{ fontSize: "1rem", color: "primary.main" }}
                          />
                        </IconButton>
                      )}

                      <Box sx={{ display: "flex", gap: 0.6 }}>
                        {/* Edit */}
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEdit(po);
                          }}
                          disabled={po.approval_status === "Approved"}
                          sx={{
                            bgcolor: (t) =>
                              po.approval_status === "Approved"
                                ? "action.disabledBackground"
                                : t.palette.mode === "dark"
                                ? alpha(t.palette.primary.main, 0.15)
                                : "#e3f2fd",
                            borderRadius: "6px",
                            width: 32,
                            height: 32,
                            transition: "all 0.2s",
                            "&:hover": {
                              bgcolor: (t) =>
                                t.palette.mode === "dark"
                                  ? alpha(t.palette.primary.main, 0.28)
                                  : "#bbdefb",
                              transform: "scale(1.05)",
                            },
                          }}
                        >
                          <Edit
                            sx={{
                              fontSize: "1rem",
                              color:
                                po.approval_status === "Approved"
                                  ? "action.disabled"
                                  : "primary.main",
                            }}
                          />
                        </IconButton>

                        {/* Delete */}
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            setAnchorEl(e.currentTarget);
                            setPoToDelete(po);
                          }}
                          disabled={po.approval_status === "Approved"}
                          sx={{
                            bgcolor: (t) =>
                              po.approval_status === "Approved"
                                ? "action.disabledBackground"
                                : t.palette.mode === "dark"
                                ? alpha(t.palette.error.main, 0.15)
                                : "#ffebee",
                            borderRadius: "6px",
                            width: 32,
                            height: 32,
                            transition: "all 0.2s",
                            "&:hover": {
                              bgcolor: (t) =>
                                t.palette.mode === "dark"
                                  ? alpha(t.palette.error.main, 0.28)
                                  : "#ffcdd2",
                              transform: "scale(1.05)",
                            },
                          }}
                        >
                          <Delete
                            sx={{
                              fontSize: "1rem",
                              color:
                                po.approval_status === "Approved"
                                  ? "action.disabled"
                                  : "error.main",
                            }}
                          />
                        </IconButton>

                        {/* Print */}
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handlePrint(po);
                          }}
                          sx={{
                            bgcolor: (t) =>
                              t.palette.mode === "dark"
                                ? alpha("#7b1fa2", 0.15)
                                : "#f3e5f5",
                            borderRadius: "6px",
                            width: 32,
                            height: 32,
                            transition: "all 0.2s",
                            "&:hover": {
                              bgcolor: (t) =>
                                t.palette.mode === "dark"
                                  ? alpha("#7b1fa2", 0.28)
                                  : "#e1bee7",
                              transform: "scale(1.05)",
                            },
                          }}
                        >
                          <Print
                            sx={{ fontSize: "1rem", color: "#7b1fa2" }}
                          />
                        </IconButton>
                      </Box>
                    </Box>
                  </Box>
                </Collapse>

                <Divider />
              </Box>
            ))}
          </Box>
        ) : (
          /* ── DESKTOP TABLE ── */
          <TableContainer
            component={Paper}
            elevation={0}
            sx={{
              overflow: "visible",
              bgcolor: "background.paper",
              backgroundImage: "none",
              border: "1px solid",
              borderColor: "divider",
              boxShadow: isDark ? "0 4px 18px rgba(0,0,0,0.35)" : "none",
            }}
          >
            <Table
              stickyHeader
              size="small"
              sx={{
                minWidth: "max-content",
                "& td, & th": {
                  borderBottom: "1px solid",
                  borderColor: "divider",
                  whiteSpace: "nowrap",
                },
              }}
            >
              <TableHead
                sx={{
                  "& th": {
                    fontWeight: "bold",
                    fontSize: "0.75rem",
                    color: "text.primary",
                    backgroundColor: tableHeadBg,
                    borderBottom: "2px solid",
                    borderColor: "primary.main",
                    letterSpacing: "0.04em",
                  },
                }}
              >
                <TableRow>
                  {userRole != "Staff" && (
                    <TableCell
                      sx={{
                        position: "sticky",
                        left: 0,
                        backgroundColor: tableHeadBg,
                        zIndex: 3,
                        minWidth: 80,
                      }}
                      align="center"
                    >
                      View
                    </TableCell>
                  )}
                  <TableCell
                    sx={{
                      position: "sticky",
                      left: userRole != "Staff" ? 80 : 0,
                      backgroundColor: tableHeadBg,
                      zIndex: 3,
                      minWidth: 80,
                    }}
                    align="center"
                  >
                    S.No.
                  </TableCell>
                  <TableCell
                    sx={{
                      position: "sticky",
                      left: userRole != "Staff" ? 160 : 80,
                      backgroundColor: tableHeadBg,
                      zIndex: 3,
                      minWidth: 120,
                    }}
                    align="center"
                  >
                    PO Number
                  </TableCell>
                  <TableCell align="center">Supplier</TableCell>
                  <TableCell align="center">Status</TableCell>
                  <TableCell align="center">Billing Address</TableCell>
                  <TableCell align="center">Shipping Address</TableCell>
                  <TableCell align="center">Created At</TableCell>
                  <TableCell align="center">Created By</TableCell>
                  <TableCell
                    sx={{
                      position: "sticky",
                      right: 0,
                      backgroundColor: tableHeadBg,
                      zIndex: 4,
                    }}
                    align="center"
                  >
                    Action
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {projects.map((po, index) => (
                  <React.Fragment key={po.id}>
                    <TableRow
                      hover
                      sx={{
                        backgroundColor: rowBg(index),
                        transition: "background-color 0.15s ease",
                        "&:hover": {
                          backgroundColor: (t) =>
                            t.palette.mode === "dark"
                              ? `${alpha(
                                  t.palette.primary.main,
                                  0.1
                                )} !important`
                              : "#e3f2fd !important",
                          "& td": {
                            backgroundColor: "inherit !important",
                          },
                        },
                        "& td": {
                          paddingRight: "13px !important",
                          paddingLeft: "13px !important",
                          padding: "0px",
                          fontSize: { xs: "0.8rem", sm: "0.75rem" },
                          whiteSpace: "nowrap",
                          color: "text.primary",
                        },
                      }}
                    >
                      {userRole != "Staff" && (
                        <TableCell
                          sx={{
                            position: "sticky",
                            left: 0,
                            bgcolor: "inherit",
                            zIndex: 2,
                            minWidth: 80,
                          }}
                          align="center"
                        >
                          <IconButton
                            size="small"
                            onClick={() => {
                              setApproveAnchorEl(true);
                              setPoToApprove(po);
                            }}
                            sx={{ padding: 0 }}
                          >
                            <Visibility
                              sx={{ fontSize: "1rem", color: "primary.main" }}
                              transform="scale(0.8)"
                            />
                          </IconButton>
                        </TableCell>
                      )}

                      <TableCell
                        sx={{
                          position: "sticky",
                          left: userRole != "Staff" ? 80 : 0,
                          bgcolor: "inherit",
                          zIndex: 2,
                          minWidth: 80,
                        }}
                        align="center"
                      >
                        {index + 1}
                      </TableCell>

                      <TableCell
                        sx={{
                          position: "sticky",
                          left: userRole != "Staff" ? 160 : 80,
                          bgcolor: "inherit",
                          zIndex: 2,
                          minWidth: 120,
                        }}
                        align="center"
                      >
                        {po.po_number}
                      </TableCell>

                      <TableCell align="center">{po.supplier_name}</TableCell>
                      <TableCell align="center">
                        <StatusChip status={po.status} />
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title={po.billing_address} arrow>
                          <span>{truncateText(po.billing_address, 20)}</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="center">
                        {po.shipping_address}
                      </TableCell>
                      <TableCell>{formatOnlyDate(po.created_at)}</TableCell>
                      <TableCell align="center">
                        {po.created_by_name ? po.created_by_name : "N/A"}
                      </TableCell>

                      <TableCell
                        sx={{
                          position: "sticky",
                          right: 0,
                          bgcolor: "inherit",
                          zIndex: 3,
                        }}
                        align="center"
                      >
                        <IconButton
                          sx={{
                            padding: 0,
                            paddingLeft: 1,
                            paddingRight: 1,
                          }}
                          size="small"
                          color="primary"
                          onClick={() => handleEdit(po)}
                        >
                          <Edit transform="scale(0.6)" />
                        </IconButton>
                        <IconButton
                          sx={{
                            padding: 0,
                            paddingLeft: 1,
                            paddingRight: 1,
                          }}
                          onClick={(e) => {
                            setAnchorEl(e.currentTarget);
                            setPoToDelete(po);
                          }}
                          color="error"
                          size="small"
                        >
                          <Delete transform="scale(0.6)" />
                        </IconButton>
                        <IconButton
                          sx={{
                            padding: 0,
                            paddingLeft: 1,
                            paddingRight: 1,
                          }}
                          size="small"
                          color="primary"
                          onClick={() => handlePrint(po)}
                        >
                          <Print transform="scale(0.6)" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </InfiniteScroll>

      <ToastContainer
        position="top-right"
        autoClose={3000}
        theme={isDark ? "dark" : "light"}
      />

      {/* ── Approve Preview Popover ── */}
      <Popover
        open={Boolean(approveAnchorEl)}
        onClose={() => {
          setApproveAnchorEl(null);
          setPoToApprove(null);
          setRemarkInput("");
        }}
        anchorReference="anchorPosition"
        anchorPosition={{
          top: window.innerHeight / 2,
          left: window.innerWidth / 2,
        }}
        transformOrigin={{ vertical: "center", horizontal: "center" }}
        PaperProps={{
          sx: {
            width: 900,
            height: 600,
            borderRadius: 2,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            bgcolor: "background.paper",
            backgroundImage: "none",
            border: "1px solid",
            borderColor: "divider",
            boxShadow: isDark
              ? "0 12px 40px rgba(0,0,0,0.65)"
              : "0 8px 32px rgba(0,0,0,0.2)",
          },
        }}
      >
        {poToApprove && (
          <>
            <Box
              sx={{
                py: 1,
                px: 2,
                borderBottom: "1px solid",
                borderColor: "divider",
                bgcolor: (t) =>
                  t.palette.mode === "dark"
                    ? alpha(t.palette.primary.main, 0.12)
                    : "#f4f6f8",
                flexShrink: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Typography
                variant="h6"
                fontWeight={700}
                sx={{ color: "text.primary" }}
              >
                Purchase Order Preview
              </Typography>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                PO Number: {poToApprove?.po_number || "-"}
              </Typography>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                <strong>Date:</strong>{" "}
                {poToApprove?.created_at
                  ? new Date(poToApprove.created_at).toLocaleDateString("en-IN")
                  : "-"}
              </Typography>
            </Box>

            <Box
              sx={{
                p: 2,
                overflowY: "auto",
                flex: 1,
                bgcolor: "background.paper",
              }}
            >
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  border: "1px solid",
                  borderColor: "divider",
                  mb: 2,
                }}
              >
                <Box
                  sx={{
                    p: 2,
                    borderRight: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <Typography
                    fontWeight={600}
                    sx={{ color: "text.primary" }}
                  >
                    Details of Recipient (Billed to)
                  </Typography>
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    Name: {poToApprove?.billed_to_name || "-"}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    Address: {poToApprove?.billed_to_address || "-"}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    GSTIN: {poToApprove?.billed_to_gst_no || "-"}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    Contact: {poToApprove?.billed_to_contact || "-"}
                  </Typography>
                </Box>
                <Box sx={{ p: 2 }}>
                  <Typography
                    fontWeight={600}
                    sx={{ color: "text.primary" }}
                  >
                    Details of Supplier (Billed from)
                  </Typography>
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    Name: {poToApprove?.billing_name || "-"}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    Address: {poToApprove?.billing_address || "-"}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    GSTIN: {poToApprove?.billing_gst || "-"}
                  </Typography>
                </Box>
              </Box>

              <Table
                size="small"
                sx={{
                  border: "1px solid",
                  borderColor: "divider",
                  "& td, & th": {
                    borderBottom: "1px solid",
                    borderColor: "divider",
                    color: "text.primary",
                  },
                }}
              >
                <TableHead>
                  <TableRow
                    sx={{
                      bgcolor: tableHeadBg,
                      "& th": {
                        fontWeight: 700,
                        color: "text.primary",
                      },
                    }}
                  >
                    <TableCell>S No.</TableCell>
                    <TableCell>Item</TableCell>
                    <TableCell>Product Type</TableCell>
                    <TableCell>Unit</TableCell>
                    <TableCell>Quantity</TableCell>
                    <TableCell>Rate</TableCell>
                    <TableCell>Amount</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {poToApprove?.items?.length > 0 ? (
                    <>
                      {poToApprove.items.map((item, index) => (
                        <TableRow
                          key={item.id || index}
                          sx={{
                            bgcolor: rowBg(index),
                          }}
                        >
                          <TableCell>{index + 1}</TableCell>
                          <TableCell>{item.item_name || "-"}</TableCell>
                          <TableCell>{item.size_name || "-"}</TableCell>
                          <TableCell>{item.unit_name || "-"}</TableCell>
                          <TableCell>{item.quantity || 0}</TableCell>
                          <TableCell>{item.rate || 0}</TableCell>
                          <TableCell>{item.amount || 0}</TableCell>
                        </TableRow>
                      ))}
                      <TableRow>
                        <TableCell
                          colSpan={6}
                          align="left"
                          sx={{
                            fontWeight: 700,
                            borderTop: "2px solid",
                            borderColor: "divider",
                            color: "text.primary",
                          }}
                        >
                          Total:
                        </TableCell>
                        <TableCell
                          sx={{
                            fontWeight: 700,
                            borderTop: "2px solid",
                            borderColor: "divider",
                            color: "text.primary",
                          }}
                        >
                          ₹{" "}
                          {poToApprove?.items
                            ?.reduce(
                              (sum, item) => sum + (item.amount || 0),
                              0
                            )
                            ?.toLocaleString("en-IN") || 0}
                        </TableCell>
                      </TableRow>
                    </>
                  ) : (
                    <TableRow>
                      <TableCell
                        colSpan={7}
                        align="center"
                        sx={{ color: "text.secondary" }}
                      >
                        No Items Available
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Box>

            <Box
              sx={{
                p: 2,
                borderTop: "1px solid",
                borderColor: "divider",
                bgcolor: (t) =>
                  t.palette.mode === "dark"
                    ? alpha(t.palette.common.white, 0.03)
                    : "#fafafa",
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
                gap: 1,
                flexShrink: 0,
              }}
            >
              <Button
                variant="outlined"
                onClick={() => {
                  setApproveAnchorEl(null);
                  setPoToApprove(null);
                  setRemarkInput("");
                }}
              >
                Close
              </Button>
            </Box>
          </>
        )}
      </Popover>

      {/* ── Delete Popover ── */}
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => {
          setAnchorEl(null);
          setPoToDelete(null);
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{
          sx: {
            bgcolor: "background.paper",
            backgroundImage: "none",
            border: "1px solid",
            borderColor: "divider",
            boxShadow: isDark
              ? "0 8px 28px rgba(0,0,0,0.6)"
              : "0 4px 16px rgba(0,0,0,0.14)",
          },
        }}
      >
        <Box sx={{ p: 2, minWidth: 200 }}>
          <Typography
            variant="body2"
            gutterBottom
            sx={{ color: "text.primary" }}
          >
            Are you sure you want to delete{" "}
            <strong>{poToDelete?.name}</strong>?
          </Typography>
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 1,
              mt: 1,
            }}
          >
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                setAnchorEl(null);
                setPoToDelete(null);
              }}
            >
              Close
            </Button>
            <Button
              size="small"
              color="error"
              variant="contained"
              onClick={async () => {
                await confirmDelete(poToDelete);
                setAnchorEl(null);
                setPoToDelete(null);
              }}
            >
              Delete
            </Button>
          </Box>
        </Box>
      </Popover>
    </Box>
  );
};

export default ProjectList;